
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogOut, User, File, MessageSquare, Settings2 } from "lucide-react";
import { useState, useEffect } from "react";
import LanguageSelector from "@/components/frank/LanguageSelector";
import { useLanguage } from "@/contexts/LanguageContext";
import { useManagerAuth } from "@/hooks/useManagerAuth";
import { getOrganizationName } from "@/services/organization";
import UserAvatar from "@/components/frank/UserAvatar";

const TopNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const { t } = useLanguage();
  
  const { member, organizationId, signOut, isLoading, isMemberAuthenticated } = useManagerAuth();
  
  // Check if this is a public route first
  const isPublicRoute = ['/', '/priser', '/funktioner', '/kontakt', '/auth', '/forgot-password', '/reset-password'].includes(location.pathname);

  useEffect(() => {
    if (member && member.role === 'ADMIN') {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }
  }, [member]);

  useEffect(() => {
    const fetchOrganizationName = async () => {
      if (organizationId) {
        try {
          const orgData = await getOrganizationName(organizationId);
          setOrganizationName(orgData?.name || null);
        } catch (error) {
          console.error('Error fetching organization name:', error);
          setOrganizationName(null);
        }
      } else {
        setOrganizationName(null);
      }
    };

    fetchOrganizationName();
  }, [organizationId]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const isManagerRoute = location.pathname.startsWith('/manager');
  const shouldShowNav = isManagerRoute && isMemberAuthenticated;

  // Don't render anything on public routes (they have their own headers)
  if (isPublicRoute) return null;

  return (
    <header 
      className="flex items-center justify-between whitespace-nowrap border-b border-solid border-[#f0f2f4] px-4 md:px-10 py-3 bg-white sticky top-0 z-50"
      style={{ fontFamily: 'Newsreader, "Noto Sans", sans-serif' }}
    >
      <div className="flex items-center gap-4 text-[#1B365D]">
        <div className="size-4">
          <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12.0799 24L4 19.2479L9.95537 8.75216L18.04 13.4961L18.0446 4H29.9554L29.96 13.4961L38.0446 8.75216L44 19.2479L35.92 24L44 28.7521L38.0446 39.2479L29.96 34.5039L29.9554 44H18.0446L18.04 34.5039L9.95537 39.2479L4 28.7521L12.0799 24Z"
              fill="currentColor"
            />
          </svg>
        </div>
        <h2 className="text-[#1B365D] text-lg font-bold leading-tight tracking-[-0.015em] cursor-pointer" onClick={() => navigate('/manager/dashboard')}>
          {shouldShowNav && organizationName 
            ? organizationName 
            : "Organization Portal"}
        </h2>
      </div>

      <div className="flex flex-1 justify-end gap-4 md:gap-8">
        {shouldShowNav && (
          <>
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-9">
              <button 
                onClick={() => navigate('/manager/dashboard')} 
                className={`text-base font-medium leading-normal transition-colors ${
                  location.pathname === '/manager/dashboard' 
                    ? 'text-[#1B365D] font-bold' 
                    : 'text-[#1B365D] hover:text-[#2F80ED]'
                }`}
              >
                {t('news.latest_news')}
              </button>
              <button 
                onClick={() => navigate('/manager/documents')} 
                className={`text-base font-medium leading-normal transition-colors ${
                  location.pathname === '/manager/documents' 
                    ? 'text-[#1B365D] font-bold' 
                    : 'text-[#1B365D] hover:text-[#2F80ED]'
                }`}
              >
                {t('nav.documents')}
              </button>
              <button 
                onClick={() => navigate('/manager/ask-frank')} 
                className={`text-base font-medium leading-normal transition-colors ${
                  location.pathname === '/manager/ask-frank' 
                    ? 'text-[#1B365D] font-bold' 
                    : 'text-[#1B365D] hover:text-[#2F80ED]'
                }`}
              >
                {t('nav.ask_frank')}
              </button>
              {isAdmin && (
                <button 
                  onClick={() => navigate('/manager/admin')} 
                  className={`text-base font-medium leading-normal transition-colors ${
                    location.pathname === '/manager/admin' 
                      ? 'text-[#1B365D] font-bold' 
                      : 'text-[#1B365D] hover:text-[#2F80ED]'
                  }`}
                >
                  {t('nav.admin')}
                </button>
              )}
            </div>
          </>
        )}

        <div className="flex items-center gap-2">
          <LanguageSelector />
          {shouldShowNav && (
            <>
              {/* Desktop Sign Out and Profile */}
              <div className="hidden md:flex items-center gap-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/manager/profile')}
                  className="p-0 h-10 w-10 hover:bg-gray-100"
                >
                  <UserAvatar
                    fullName={member?.full_name}
                    profilePictureUrl={member?.profile_picture_url}
                  />
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleSignOut}
                  className="p-2 h-10 text-[#1B365D] hover:text-[#2F80ED] hover:bg-gray-100"
                  title="Sign Out"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Mobile Sign Out and Profile */}
              <div className="flex md:hidden items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/manager/profile')}
                  className="p-0 h-10 w-10 hover:bg-gray-100"
                >
                  <UserAvatar
                    fullName={member?.full_name}
                    profilePictureUrl={member?.profile_picture_url}
                  />
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleSignOut}
                  className="p-2 h-10 text-[#1B365D] hover:text-[#2F80ED] hover:bg-gray-100"
                  title="Sign Out"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopNavigation;
